import { NetworkTypes } from '@provex/eip712-utils/network-types'

/**
 * Network families whose derivers produce a DISTINCT address per index. The other
 * families (evm, tvm, memo) echo one shared root address — publication modes do not
 * apply to them (spec §3.1/§8). The set of families is closed; keep in sync with
 * packages/addresses derivers if a family is ever added.
 */
export const PER_INDEX_DERIVING_NETWORK_TYPES = ['bip32', 'cardano', 'monero', 'solana'] as const

export const derivesPerIndexAddresses = (networkType: string): boolean =>
  (PER_INDEX_DERIVING_NETWORK_TYPES as readonly string[]).includes(networkType)

export type ParsedNetwork = {
  networkType: NetworkTypes
  chainId: number
}

export type ParseNetworkResult = {
  network: ParsedNetwork
  error?: string | null
}

export const emptyNetwork = { networkType: NetworkTypes.EVM, chainId: 0 }

export const networkToKey = (network: ParsedNetwork): string => {
  return `${network.networkType}:${network.chainId}`
}

export const parseNetwork = (network: string): ParseNetworkResult => {
  // Parse and validate network format
  const networkParts = network.split(':')
  if (networkParts.length !== 2) {
    return {
      network: { ...emptyNetwork },
      error: 'Invalid network format. Expected format: type:chainId (e.g., evm:1, memo:144)',
    }
  }

  const [networkType, chainIdStr] = networkParts as [string, string]
  return validateNetworkParts(networkType, chainIdStr)
}

export const validateNetworkParts = (type: string, chainIdStr: string): ParseNetworkResult => {
  if (!type || !chainIdStr) {
    return {
      network: { ...emptyNetwork },
      error: 'Invalid network format. Expected format: type:chainId (e.g., evm:1, memo:144)',
    }
  }

  const networkType = type as NetworkTypes
  const chainId = parseInt(chainIdStr, 10)

  if (isNaN(chainId)) {
    return {
      network: { ...emptyNetwork },
      error: 'Invalid chain ID. Must be a number',
    }
  }

  if (!Object.values(NetworkTypes).includes(networkType)) {
    return {
      network: { ...emptyNetwork },
      error: `Invalid network type. Expected one of: ${Object.values(NetworkTypes).join(', ')}`,
    }
  }

  const parsedNetwork = { networkType, chainId }
  return {
    network: parsedNetwork,
  }
}
